import axios from 'axios';
import { AxiosInstance, GenericAbortSignal, InternalAxiosRequestConfig } from 'axios';

interface ApiAuthCallbacks {
  clearSession: () => void;
  getAccessToken: () => string | null;
  getTokenRevision: () => number;
  refreshToken: () => Promise<void>;
}

interface AuthRequestContext {
  callerSignal?: GenericAbortSignal;
  dispose: () => void;
  generation: number;
  isAuthEndpoint: boolean;
  isRetry: boolean;
  tokenRevision: number;
}

interface AuthRequestConfig extends InternalAxiosRequestConfig {
  authRequestContext?: AuthRequestContext;
}

interface SafeErrorResponse {
  data: unknown;
  status: number;
}

class ApiRequestError extends Error {
  readonly code?: string;
  readonly isAxiosError = true;
  readonly response?: SafeErrorResponse;

  constructor(response?: SafeErrorResponse, code?: string) {
    super('Не удалось выполнить запрос.');
    this.code = code;
    this.name = 'ApiRequestError';
    this.response = response;
  }
}

let callbacks: ApiAuthCallbacks | null = null;
let sessionController = new AbortController();
let sessionGeneration = 0;
let pendingRefresh: { generation: number; promise: Promise<void>; tokenRevision: number } | null = null;

export const registerApiAuthCallbacks = (value: ApiAuthCallbacks): void => {
  callbacks = value;
};

export const resetApiSession = (): void => {
  sessionGeneration += 1;
  sessionController.abort();
  sessionController = new AbortController();
};

const getAccessToken = (): string | null => {
  return callbacks?.getAccessToken() ?? null;
};

const getRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const getRequestSecrets = (config?: AuthRequestConfig): string[] => {
  const secrets = new Set<string>();
  const token = getAccessToken();
  if (token) secrets.add(token);
  const authorization = config?.headers?.get('Authorization');
  if (typeof authorization === 'string') secrets.add(authorization.replace(/^Bearer\s+/i, ''));
  const visit = (value: unknown, depth: number): void => {
    if (depth > 6) return;
    const record = getRecord(value);
    if (!record) return;
    for (const [key, item] of Object.entries(record).slice(0, 100)) {
      if (/token|password|authorization|secret/i.test(key) && typeof item === 'string') secrets.add(item);
      else visit(item, depth + 1);
    }
  };
  let data: unknown = config?.data;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch { data = null; }
  }
  visit(data, 0);
  visit(config?.params, 0);
  return [...secrets].filter((value) => value.length > 0);
};

const sanitizeData = (value: unknown, secrets: string[], depth = 0, visited = new WeakSet<object>()): unknown => {
  if (depth > 8) return null;
  if (typeof value === 'string') {
    const redacted = secrets.reduce((result, secret) => result.split(secret).join('[redacted]'), value);
    return redacted.replace(/\b[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]+\b/g, '[redacted]');
  }
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (!value || typeof value !== 'object' || visited.has(value)) return null;
  visited.add(value);
  if (Array.isArray(value)) return value.slice(0, 200).map((item) => sanitizeData(item, secrets, depth + 1, visited));
  if (Object.getPrototypeOf(value) !== Object.prototype) return null;
  return Object.fromEntries(Object.entries(value).slice(0, 200).map(([key, item]) => [
    key,
    /token|authorization|password|cookie|secret/i.test(key)
      ? '[redacted]'
      : sanitizeData(item, secrets, depth + 1, visited)
  ]));
};

const getSafeError = (error: unknown, config?: AuthRequestConfig): Error => {
  if (axios.isCancel(error)) return new axios.CanceledError('Запрос отменён.');
  const record = getRecord(error);
  const response = getRecord(record?.response);
  const code = typeof record?.code === 'string' && /^E[A-Z_]+$/.test(record.code) ? record.code : undefined;
  return new ApiRequestError(
    response && typeof response.status === 'number'
      ? { data: sanitizeData(response.data, getRequestSecrets(config)), status: response.status }
      : undefined,
    code
  );
};

const isSessionCurrent = (context?: AuthRequestContext): boolean => {
  return !context || context.generation === sessionGeneration;
};

const combineSignals = (
  callerSignal: GenericAbortSignal | undefined,
  shouldCancelWithSession: boolean
): { dispose: () => void; signal: AbortSignal } => {
  const controller = new AbortController();
  const signals = [callerSignal, ...(shouldCancelWithSession ? [sessionController.signal] : [])];
  const abort = (): void => controller.abort();
  for (const signal of signals) {
    if (signal?.aborted) abort();
    else signal?.addEventListener?.('abort', abort, { once: true });
  }
  return {
    dispose: () => signals.forEach((signal) => signal?.removeEventListener?.('abort', abort)),
    signal: controller.signal
  };
};

const waitForRefresh = (promise: Promise<void>, signal?: GenericAbortSignal): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const removeListener = (): void => signal?.removeEventListener?.('abort', abort);
    const abort = (): void => {
      removeListener();
      reject(new axios.CanceledError('Запрос отменён.'));
    };
    if (signal?.aborted) {
      abort();
      return;
    }
    signal?.addEventListener?.('abort', abort, { once: true });
    promise.then(() => { removeListener(); resolve(); }, (error: unknown) => { removeListener(); reject(error); });
  });
};

export const installApiAuthTransport = (client: AxiosInstance, configuredUrl: string, isAi: boolean): void => {
  const pageUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.href;
  const configuredOrigin = new URL(configuredUrl, pageUrl).origin;

  client.interceptors.request.use((request: AuthRequestConfig) => {
    const target = new URL(client.getUri(request), pageUrl);
    if (target.origin !== configuredOrigin || target.username || target.password) {
      throw new ApiRequestError(undefined, 'ERR_INVALID_URL');
    }
    const previous = request.authRequestContext;
    if (!isSessionCurrent(previous)) throw new axios.CanceledError('Запрос отменён.');
    const isAuthEndpoint = !isAi && (/^\/api\/auth\/(?:authorize|refresh|logout|password-policy|password-reset)(?:\/|$)/.test(target.pathname)
      || target.pathname === '/api/register/user' || /^\/api\/email\/verify(?:\/|$)/.test(target.pathname));
    const callerSignal = previous ? previous.callerSignal : request.signal;
    const combined = combineSignals(callerSignal, !isAuthEndpoint);
    const accessToken = getAccessToken();
    request.authRequestContext = {
      callerSignal,
      dispose: combined.dispose,
      generation: sessionGeneration,
      isAuthEndpoint,
      isRetry: previous?.isRetry ?? false,
      tokenRevision: callbacks?.getTokenRevision() ?? 0
    };
    request.signal = combined.signal;
    if (typeof FormData !== 'undefined' && request.data instanceof FormData) request.headers.set('Content-Type', false);
    else request.headers.set('Content-Type', 'application/json');
    if (!isAi) request.headers.set('Time-Zone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (accessToken && !isAuthEndpoint) request.headers.set('Authorization', `Bearer ${accessToken}`);
    else request.headers.delete('Authorization');
    return request;
  });

  client.interceptors.response.use((response) => {
    const config = response.config as AuthRequestConfig;
    const context = config.authRequestContext;
    context?.dispose();
    if (!isSessionCurrent(context)) throw new axios.CanceledError('Запрос отменён.');
    return response;
  }, async (error: unknown) => {
    const record = getRecord(error);
    const config = record?.config as AuthRequestConfig | undefined;
    const context = config?.authRequestContext;
    if (!isSessionCurrent(context) || config?.signal?.aborted || axios.isCancel(error)) {
      context?.dispose();
      throw new axios.CanceledError('Запрос отменён.');
    }
    const response = getRecord(record?.response);
    if (config && context && response?.status === 401 && !context.isAuthEndpoint && !context.isRetry
      && callbacks && getAccessToken()) {
      context.isRetry = true;
      try {
        if (context.tokenRevision === callbacks.getTokenRevision()) {
          if (!pendingRefresh || pendingRefresh.generation !== sessionGeneration
            || pendingRefresh.tokenRevision !== context.tokenRevision) {
            const operation = {
              generation: sessionGeneration,
              promise: callbacks.refreshToken(),
              tokenRevision: context.tokenRevision
            };
            pendingRefresh = operation;
            operation.promise.finally(() => {
              if (pendingRefresh === operation) pendingRefresh = null;
            }).catch(() => {});
          }
          await waitForRefresh(pendingRefresh.promise, config.signal);
        }
        if (!isSessionCurrent(context) || context.callerSignal?.aborted) {
          throw new axios.CanceledError('Запрос отменён.');
        }
        if (!getAccessToken()) throw new ApiRequestError(undefined, 'ERR_BAD_REQUEST');
        context.dispose();
        return await client.request(config);
      } catch (refreshError) {
        if (!isSessionCurrent(context) || context.callerSignal?.aborted) {
          throw new axios.CanceledError('Запрос отменён.');
        }
        throw getSafeError(refreshError, config);
      } finally {
        context.dispose();
      }
    }
    context?.dispose();
    const safeError = getSafeError(error, config);
    if (context?.isRetry && response?.status === 401 && callbacks
      && context.tokenRevision === callbacks.getTokenRevision()) callbacks.clearSession();
    throw safeError;
  });
};